import * as React from "react";
import { useBrandItemLogisticsQuery } from "../../Queries";
import PageContent from "../../layout/PageContent";
import { Box, LinearProgress, Paper, Typography } from "@mui/material";
import { DataColumn, DataTable } from "../../components/DataTable";
import { DateFormat } from "../../utility/DateFormat";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';

type BrandItemLogisticsProps = {
  brandItemId: string;
};

type VasService = {
  vasId: string;
  vasName: string;
  sortingOrder: number;
  completed: boolean;
  completedBy?: string | null;
  startDate?: string | null;
  completedDate?: string | null;
  workflowName?: string | null;
  workflowTs?: string | null;
  isCurrent: boolean;
};

type WorkflowGroup = {
  workflowTs: string | null;
  workflowName: string | null;
  services: VasService[];
};

export const BrandItemLogistics = ({ brandItemId }: BrandItemLogisticsProps) => {
  const { data, loading } = useBrandItemLogisticsQuery({
    variables: {
      brandItemId
    },
    fetchPolicy: "no-cache"
  });

  if (loading || !data) {
    return <LinearProgress />;
  }

  const allServices = (data.BrandItem?.vas || [])
    .map((service, index) => {
      const isCurrent = !service?.completed &&
        !data.BrandItem?.vas?.slice(0, index).some(s => !s?.completed);

      return {
        ...service,
        isCurrent
      } as VasService;
    });

  if (allServices.length === 0) {
    return (
      <PageContent>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">
            No value added services (VAS) found for this item.
          </Typography>
        </Paper>
      </PageContent>
    );
  }

  const groupMap = new Map<string, WorkflowGroup>();
  const UNDEFINED_KEY = "__undefined__";

  for (const service of allServices) {
    const key = service.workflowTs ? new Date(service.workflowTs).toISOString() : UNDEFINED_KEY;
    if (!groupMap.has(key)) {
      groupMap.set(key, {
        workflowTs: service.workflowTs || null,
        workflowName: service.workflowName || null,
        services: []
      });
    }
    groupMap.get(key)!.services.push(service);
  }

  const groups = Array.from(groupMap.values());
  groups.sort((a, b) => {
    if (!a.workflowTs && !b.workflowTs) return 0;
    if (!a.workflowTs) return 1;
    if (!b.workflowTs) return -1;
    return new Date(b.workflowTs).getTime() - new Date(a.workflowTs).getTime();
  });

  for (const group of groups) {
    group.services.sort((a, b) => {
      if (!a.completedDate && !b.completedDate) return (a.sortingOrder || 0) - (b.sortingOrder || 0);
      if (!a.completedDate) return 1;
      if (!b.completedDate) return -1;
      return new Date(a.completedDate).getTime() - new Date(b.completedDate).getTime();
    });
  }

  const columns: DataColumn<VasService>[] = [
    {
      field: 'vasName',
      label: 'Service',
      render: (name, row) => {
        return (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            fontWeight: row.isCurrent ? 'bold' : 'normal',
            color: row.isCurrent ? 'primary.main' : 'inherit'
          }}>
            {row.isCurrent && (
              <PendingIcon color="primary" sx={{ mr: 1 }} />
            )}
            {row.completed && (
              <CheckCircleIcon color="success" sx={{ mr: 1 }} />
            )}
            {name}
            {row.isCurrent && (
              <Typography variant="caption" sx={{ ml: 1, color: 'primary.main' }}>
                (Current)
              </Typography>
            )}
          </Box>
        );
      }
    },
    {
      field: 'startDate',
      label: 'Start Date',
      render: (date) => {
        return date ? <Box>{DateFormat.toPresent(date)}</Box> : <Box>-</Box>;
      }
    },
    {
      field: 'completedDate',
      label: 'Completed Date',
      render: (date) => {
        return date ? <Box>{DateFormat.toPresent(date)}</Box> : <Box>-</Box>;
      }
    },
    {
      field: 'completedBy',
      label: 'Completed By',
      render: (email) => {
        return email ? <Box>{email}</Box> : <Box>-</Box>;
      }
    }
  ];

  return (
    <PageContent>
      {groups.map((group, index) => (
        <Box key={group.workflowTs || 'undefined'} sx={{ mb: index < groups.length - 1 ? 3 : 0 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {group.workflowName || 'Workflow'}
            {group.workflowTs && (
              <Typography component="span" variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                ({DateFormat.toPresent(group.workflowTs)})
              </Typography>
            )}
          </Typography>
          <DataTable
            idField="vasId"
            columns={columns}
            rows={group.services}
            hideFilter
          />
        </Box>
      ))}
    </PageContent>
  );
};
